import React, { useState } from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { useParams, useNavigate } from "react-router-dom";
import WarehouseNewAssortmentCreationComponent from "./component";
import debounce from 'lodash/debounce';

const WarehouseNewAssortmentCreationContainer = (props) => {
  const { reference_id } = useParams();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [mvIdInput, setMvIdInput] = useState("");
  const [selectedMvIds, setSelectedMvIds] = useState([]);
  const [filteredMvList, setFilteredMvList] = useState([]);
  const [showMvIdDropdown, setShowMvIdDropdown] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [mvIdsInput, setMvIdsInput] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [categories, setCategories] = useState([]);
  const [verifiedMvIds, setVerifiedMvIds] = useState([]);
  const [invalidMvIds, setInvalidMvIds] = useState([]);
  const [duplicateMvIds, setDuplicateMvIds] = useState([]);
  const [alreadySelectedMvIds, setAlreadySelectedMvIds] = useState([]);
  const [showMaxItemsPopup, setShowMaxItemsPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState({ id: '', name: '' });
  const [getReferenceName, setGetReferenceName] = useState()
  const [showRuleFailurePopup, setShowRuleFailurePopup] = useState(false);
  const [ruleFailureData, setRuleFailureData] = useState([]);
  const [lastUpdatedTime, setLastUpdatedTime] = useState('');
  const [searchLoading, setSearchLoading] = useState(false); // ✅ New state for search loading

  const handleOpenUploadModal = () => {
    setOpenUploadModal(true);
    setMvIdsInput("");
    setIsVerified(false);
    setDuplicateMvIds([]);
    setAlreadySelectedMvIds([]);
  };

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false);
  };

  const handleDeleteMvId = (idToDelete) => {
    const updatedSelectedMvIds = selectedMvIds.filter(item => item.id !== idToDelete.id);
    setSelectedMvIds(updatedSelectedMvIds);
    updateCategories(updatedSelectedMvIds);
  };

  // ✅ Updated search functionality (same as rule creation)
  const handleMvIdInputChange = (e) => {
    const value = e.target.value;
    // Allow alphanumeric + spaces for search
    if (/^[a-zA-Z0-9\s]*$/.test(value) || value === "") {
      setMvIdInput(value);
      handleSearchMvId(value);
    }
  };

  // ✅ Updated search function with debounce (same as rule creation)
  const handleSearchMvId = debounce((query = "") => {
    if (query.trim() === "") {
      setFilteredMvList([]);
      setShowMvIdDropdown(false);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    setShowMvIdDropdown(true);

    props.getDataFromAPI(
      `/dashboard/api/v2/admin/mvs/all?limit=50&offset=0&isActive=1&search=${encodeURIComponent(query)}`,
      "GET",
      undefined,
      (response) => {
        const results = response?.list?.map(item => ({
          variant_id: item.id,
          id: item.id,
          variant_name: item.variant_name,
          mrp: item.mrp || item.offer_price || 0,
          image_id: item.image_id || null,
          categories: item.item_categories || []
        })) || [];
        
        setFilteredMvList(results);
        setSearchLoading(false);
      },
      (err) => {
        console.error("MVID search error:", err);
        setFilteredMvList([]);
        setSearchLoading(false);
        setShowMvIdDropdown(false);
      }
    );
  }, 300);

  const handleCreateNewAssortment = async () => {
    if (!startDate) {
      toastr.error("Error", "Please select a start date");
      return;
    }

    if (selectedMvIds.length === 0) {
      toastr.error("Error", "Please add at least one MV ID");
      return;
    }

    const priorityItems = selectedMvIds
      .map(mv => ({ mv_id: parseInt(mv.id, 10) }))
      .filter(item => !isNaN(item.mv_id));

    const payload = {
      type: "warehouse",
      reference_id: parseInt(reference_id),
      start_date: startDate,
      priority_wise_items: priorityItems,
    };

    props.getDataFromAPI(
      `/partner/api/v1/assortment/create`,
      "POST",
      payload,
      (response) => {
        if (response?.id) {
          // Success case
          setSuccessData({
            id: response.id,
            name: response.reference_name
          });
          setShowSuccessPopup(true);
          setStartDate("");
          setSelectedMvIds([]);
          setFilteredMvList([]);
          setMvIdInput("");
        } else if (Array.isArray(response)) {
          // Handle rule failures array
          if (response.length > 0) {
            setRuleFailureData(response);
            setLastUpdatedTime(new Date().toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            }));
            setShowRuleFailurePopup(true);
          } else {
            toastr.error("Error", "No items could be added to the assortment");
          }
        } else {
          // Fallback error handling
          const errorMsg = response?.message || 'Failed to create assortment';
          toastr.error("Error", errorMsg);
        }
      },
      (err) => {
        toastr.error("Error", err.message || 'Failed to create assortment');
      }
    );
  };

  const updateCategories = (mvItems) => {
    const categoryMap = {};

    mvItems.forEach(item => {
      const categories = item.categories || [];
      categories.forEach(categoryObj => {
        const categoryName = categoryObj.name || categoryObj.categoryName || 'Uncategorized';
        if (!categoryMap[categoryName]) {
          categoryMap[categoryName] = {
            count: 0,
            items: [],
            image: categoryObj.image || categoryObj.categoryImage
          };
        }
        categoryMap[categoryName].items.push(item);
        categoryMap[categoryName].count++;
      });
    });

    const categoriesArray = Object.keys(categoryMap).map(categoryName => ({
      name: categoryName,
      count: categoryMap[categoryName].count,
      items: categoryMap[categoryName].items,
      image: categoryMap[categoryName].image
    }));

    setCategories(categoriesArray);
  };

  const handleAddMvId = (mv) => {
    const idToAdd = mv?.id || mvIdInput.trim();
    if (!idToAdd) return;

    if (selectedMvIds.some(item => item.id === idToAdd)) {
      toastr.warning("Warning", "This MV ID is already added");
      return;
    }

    const newItem = mv?.id ? {
      id: mv.id.toString(),
      variant_name: mv.variant_name || 'Unknown Product',
      mrp: mv.mrp || mv.offer_price || 0,
      image_id: mv.image_id || null,
      categories: mv.categories || []
    } : {
      id: idToAdd,
      variant_name: "Unknown Product",
      mrp: 0,
      image_id: null,
      categories: []
    };

    const updatedSelectedMvIds = [...selectedMvIds, newItem];
    setSelectedMvIds(updatedSelectedMvIds);
    updateCategories(updatedSelectedMvIds);
    setMvIdInput("");
    setShowMvIdDropdown(false);
    setFilteredMvList([]);
  };

  // Handle bulk MV IDs paste (Excel/Sheet se copy paste)
  const handleBulkMvIdsPaste = (e) => {
    e.preventDefault();

    // Get raw data from clipboard
    let pasteData = e.clipboardData.getData("Text");

    // Replace newlines, tabs, multiple spaces with commas
    pasteData = pasteData
      .trim()
      .replace(/[\n\r\t]+/g, ",")   // newline / tab → comma
      .replace(/\s+/g, ",")         // spaces → comma
      .replace(/,+/g, ",");         // multiple commas → single comma

    // Remove leading zeros & empty values
    const cleaned = pasteData
      .split(",")
      .map((id) => id.replace(/^0+/, "").trim()) // remove leading 0s
      .filter((id) => id.length > 0)
      .join(",");

    setMvIdsInput(cleaned);
  };

  const handleCheckMvIds = async () => {
    try {
      if (!mvIdsInput.trim()) {
        toastr.error("Error", "Please enter MV IDs to verify");
        return;
      }

      const inputIds = mvIdsInput.split(/[,\s]+/).filter(id => id !== '');

      // Check for duplicates in the input
      const inputDuplicates = [];
      const uniqueInputIds = [];
      const seenIds = new Set();

      inputIds.forEach(id => {
        if (seenIds.has(id)) {
          inputDuplicates.push(id);
        } else {
          seenIds.add(id);
          uniqueInputIds.push(id);
        }
      });

      // Check for duplicates with already selected items
      const selectedIds = selectedMvIds.map(item => item.id);
      const duplicatesWithSelected = uniqueInputIds.filter(id =>
        selectedIds.includes(id)
      );

      // Combine all duplicates
      const allDuplicateIds = [...inputDuplicates, ...duplicatesWithSelected];
      setDuplicateMvIds(inputDuplicates);
      setAlreadySelectedMvIds(duplicatesWithSelected);

      // Convert to numbers for API call
      const numericIds = uniqueInputIds
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id));

      if (numericIds.length === 0) {
        toastr.error("Error", "No valid numeric MV IDs found");
        return;
      }

      const payload = { mvIds: numericIds };

      const response = await new Promise((resolve, reject) => {
        props.getDataFromAPI(
          `/partner/api/v1/assortment/items/check/all`,
          "POST",
          payload,
          (successResponse) => resolve(successResponse),
          (error) => reject(error)
        );
      });

      if (response && response.mvPresent) {
        const validMvIds = [];
        const invalidMvIds = [...allDuplicateIds]; // Start with duplicates
        const categoryMap = {};

        response.mvPresent.forEach(item => {
          if (item.isPresent && !duplicatesWithSelected.includes(item.mvId.toString())) {
            const mvItem = {
              id: item.mvId.toString(),
              variant_name: item.name || 'Unknown Product',
              mrp: item.offerPrice || 0,
              image_id: item.image || null,
              categories: item.categories || []
            };
            validMvIds.push(mvItem);

            // Process categories
            const categories = item.categories || [];
            categories.forEach(categoryObj => {
              const categoryName = categoryObj.categoryName || 'Uncategorized';
              if (!categoryMap[categoryName]) {
                categoryMap[categoryName] = {
                  count: 0,
                  items: [],
                  image: categoryObj.categoryImage
                };
              }
              categoryMap[categoryName].items.push(mvItem);
              categoryMap[categoryName].count++;
            });
          } else if (!duplicatesWithSelected.includes(item.mvId.toString())) {
            invalidMvIds.push(item.mvId.toString());
          }
        });

        const categoriesArray = Object.keys(categoryMap).map(categoryName => ({
          name: categoryName,
          count: categoryMap[categoryName].count,
          items: categoryMap[categoryName].items,
          image: categoryMap[categoryName].image
        }));

        setVerifiedMvIds(validMvIds);
        setInvalidMvIds(invalidMvIds);
        setCategories(categoriesArray);
        setIsVerified(true);

        // Show warnings about duplicates
        if (inputDuplicates.length > 0 || duplicatesWithSelected.length > 0) {
          let message = '';
          if (inputDuplicates.length > 0) {
            message += `${inputDuplicates.length} duplicate MV IDs in your input. `;
          }
          if (duplicatesWithSelected.length > 0) {
            message += `${duplicatesWithSelected.length} MV IDs already exist in your selection. `;
          }
          message += 'These have been marked as invalid.';
          toastr.warning("Warning", message);
        }

        toastr.success(
          "Success",
          `${validMvIds.length} valid MV IDs found, ${invalidMvIds.length} invalid`
        );
      } else {
        toastr.error("Error", "No valid MV IDs found");
      }
    } catch (err) {
      toastr.error("Error", "Failed to verify MV IDs");
    }
  };

  const handleRemoveInvalidMvId = (mvIdToRemove) => {
    const idToRemove = mvIdToRemove.toString();

    // Update invalid MV IDs list
    const updatedInvalidMvIds = invalidMvIds.filter(id =>
      id.toString() !== idToRemove
    );
    setInvalidMvIds(updatedInvalidMvIds);

    // Update duplicate lists if needed
    setDuplicateMvIds(duplicateMvIds.filter(id => id !== idToRemove));
    setAlreadySelectedMvIds(alreadySelectedMvIds.filter(id => id !== idToRemove));

    // Update the input text
    const updatedInput = mvIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id !== idToRemove)
      .join(', ');

    setMvIdsInput(updatedInput);
  };

  const handleContinue = () => {
    if (verifiedMvIds.length === 0) {
      toastr.error("Error", "No valid MV IDs to add");
      return;
    }

    // Filter out any MV IDs that are already in selectedMvIds
    const newItems = verifiedMvIds.filter(newItem =>
      !selectedMvIds.some(existingItem => existingItem.id === newItem.id)
    );

    if (newItems.length > 0) {
      const updatedSelectedMvIds = [...selectedMvIds, ...newItems];
      setSelectedMvIds(updatedSelectedMvIds);
      updateCategories(updatedSelectedMvIds);
      toastr.success("Success", `${newItems.length} MV IDs added successfully`);
    } else {
      toastr.info("Info", "No new MV IDs to add (all were duplicates)");
    }

    handleCloseUploadModal();
  };

  const handleResetVerification = () => {
    setIsVerified(false);
    setVerifiedMvIds([]);
    setInvalidMvIds([]);
    setDuplicateMvIds([]);
    setAlreadySelectedMvIds([]);
    setMvIdsInput("");
  };

  const handleCloseMaxItemsPopup = () => {
    setShowMaxItemsPopup(false);
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    navigate(-1);
  };

  return (
    <>
      <WarehouseNewAssortmentCreationComponent
        startDate={startDate}
        setStartDate={setStartDate}
        mvIdInput={mvIdInput}
        handleMvIdInputChange={handleMvIdInputChange}
        selectedMvIds={selectedMvIds}
        handleAddMvId={handleAddMvId}
        handleDeleteMvId={handleDeleteMvId}
        filteredMvList={filteredMvList}
        showMvIdDropdown={showMvIdDropdown}
        setShowMvIdDropdown={setShowMvIdDropdown}
        handleCreateNewAssortment={handleCreateNewAssortment}
        openUploadModal={openUploadModal}
        handleOpenUploadModal={handleOpenUploadModal}
        handleCloseUploadModal={handleCloseUploadModal}
        mvIdsInput={mvIdsInput}
        setMvIdsInput={setMvIdsInput}
        isVerified={isVerified}
        handleCheckMvIds={handleCheckMvIds}
        handleContinue={handleContinue}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        categories={categories}
        handleBulkMvIdsPaste={handleBulkMvIdsPaste}
        handleResetVerification={handleResetVerification}
        verifiedMvIds={verifiedMvIds}
        invalidMvIds={invalidMvIds}
        handleRemoveInvalidMvId={handleRemoveInvalidMvId}
        duplicateMvIds={duplicateMvIds}
        alreadySelectedMvIds={alreadySelectedMvIds}
        showMaxItemsPopup={showMaxItemsPopup}
        handleCloseMaxItemsPopup={handleCloseMaxItemsPopup}
        showSuccessPopup={showSuccessPopup}
        successData={successData}
        handleCloseSuccessPopup={handleCloseSuccessPopup}
        getReferenceName={getReferenceName}
        showRuleFailurePopup={showRuleFailurePopup}
        setShowRuleFailurePopup={setShowRuleFailurePopup}
        ruleFailureData={ruleFailureData}
        lastUpdatedTime={lastUpdatedTime}
        setVerifiedMvIds={setVerifiedMvIds}
        searchLoading={searchLoading} // ✅ Pass search loading state
      />
    </>
  );
};

function mapStateToProps(props) {
  return {
    props
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(WarehouseNewAssortmentCreationContainer);